// ---------------------------------------------------------------------------
// CreateView — workspace creation form rendered inside the drawer.
// ---------------------------------------------------------------------------

import {
  Button,
  Card,
  Field,
  Input,
  Radio,
  RadioGroup,
  Textarea,
  Toolbar,
} from '@fluentui/react-components';
import { useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useWorkspaceDrawer } from '../../context/WorkspaceDrawerContext.js';
import { PrivateWorkspaceCreateSection } from '../../features/private-workspaces/index.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';
import { WorkspaceService } from '../../services/workspaceService.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionOutcome,
  EntityType,
  getOperationActionScenario,
  OperationType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';
import {
  WORKSPACE_NAME_MAX_LENGTH,
  isValidWorkspaceName,
} from '../../types/workspace.js';
import type { Workspace } from '../../types/workspace.js';

import { useStyles } from './WorkspaceDrawer.style.js';

const PUBLIC_VISIBILITY = 'public';
const PRIVATE_VISIBILITY = 'private';

type WorkspaceVisibility = typeof PUBLIC_VISIBILITY | typeof PRIVATE_VISIBILITY;

export function CreateView() {
  const styles = useStyles();
  const { workspaces, refresh } = useWorkspaces();
  const { setActiveName } = useWorkspaceContext();
  const { open } = useWorkspaceDrawer();
  const httpClient = useHttpClient();
  const service = useMemo(() => new WorkspaceService(httpClient), [httpClient]);
  const { recordAction } = useUserBi();

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] =
    useState<WorkspaceVisibility>(PUBLIC_VISIBILITY);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const nameError = useMemo<string | null>(() => {
    if (name.length === 0) return null;
    if (!isValidWorkspaceName(name)) {
      return 'Use lowercase letters, digits, and hyphens only. Must start with a letter.';
    }
    if (workspaces.some((w) => w.name === name)) {
      return 'A workspace with this name already exists.';
    }
    return null;
  }, [name, workspaces]);

  const canSubmit =
    isValidWorkspaceName(name) && nameError === null && !submitting;

  const finishCreation = async (workspace: Workspace) => {
    await refresh();
    setActiveName(workspace.name);
    open({ kind: 'list' });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      recordAction({
        panelType: PanelType.WorkspaceDrawer,
        panelRegion: PanelRegion.Overlay,
        actionScenario: getOperationActionScenario(
          EntityType.Workspace,
          OperationType.Create,
        ),
        actionScenarioType: OperationType.Create,
        actionGesture: 'click',
        actionOutcome: ActionOutcome.Requested,
      });
      const created = await service.create(name, {
        displayName: displayName.trim() || undefined,
        description: description.trim() || undefined,
      });
      await finishCreation(created);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to create workspace',
      );
      setSubmitting(false);
    }
  };

  return (
    <Card className={styles.body}>
      <Field label="Workspace access" required className={styles.field}>
        <RadioGroup
          layout="horizontal"
          value={visibility}
          onChange={(_event, data) => {
            if (
              data.value === PUBLIC_VISIBILITY ||
              data.value === PRIVATE_VISIBILITY
            ) {
              setVisibility(data.value);
              setSubmitError(null);
            }
          }}
        >
          <Radio
            value={PUBLIC_VISIBILITY}
            label="Public"
            disabled={submitting}
          />
          <Radio
            value={PRIVATE_VISIBILITY}
            label="Private"
            disabled={submitting}
          />
        </RadioGroup>
      </Field>

      <Field
        label="Name"
        className={styles.field}
        validationMessage={
          nameError ??
          'Lowercase letters, digits, and hyphens. Immutable after creation.'
        }
        validationState={nameError === null ? 'none' : 'error'}
      >
        <Input
          value={name}
          placeholder="my-team"
          maxLength={WORKSPACE_NAME_MAX_LENGTH}
          onChange={(_e, data) => setName(data.value)}
        />
      </Field>

      <Field
        label="Display name (optional)"
        className={styles.field}
        hint="Human-readable label. Can be changed later."
      >
        <Input
          value={displayName}
          placeholder="My Team"
          onChange={(_e, data) => setDisplayName(data.value)}
        />
      </Field>

      <Field label="Description (optional)" className={styles.field}>
        <Textarea
          value={description}
          rows={3}
          onChange={(_e, data) => setDescription(data.value)}
        />
      </Field>

      {visibility === PRIVATE_VISIBILITY && (
        <PrivateWorkspaceCreateSection
          name={name}
          displayName={displayName.trim() || undefined}
          description={description.trim() || undefined}
          canCreate={canSubmit}
          onCreated={(workspace) => {
            void finishCreation(workspace);
          }}
        />
      )}

      {visibility === PUBLIC_VISIBILITY && submitError !== null && (
        <Field validationState="error" validationMessage={submitError} />
      )}

      <Toolbar className={styles.actions}>
        <Button
          appearance="secondary"
          onClick={() => open({ kind: 'list' })}
          disabled={submitting}
        >
          Cancel
        </Button>
        {visibility === PUBLIC_VISIBILITY && (
          <Button
            appearance="primary"
            disabled={!canSubmit}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        )}
      </Toolbar>
    </Card>
  );
}
